import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useUser } from '../../../context/UserContext';
import { useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import CreateLeadModal from '../../../components/agent/leads/CreateLeadModal';
import ConvertLeadWithTripModal from '../../../components/agent/leads/ConvertLeadWithTripModal';
import DeleteConfirmationModal from '../../../components/agent/DeleteConfirmationModal';
import { toast } from '../../../utils/toast';
import { 
  Users, 
  Mail, 
  Phone, 
  Calendar, 
  MapPin, 
  Filter,
  Search,
  ChevronDown,
  Eye,
  UserCheck,
  UserX,
  RefreshCw,
  Plus,
  Download,
  MoreVertical,
  AlertCircle,
  X,
  Link,
  Copy,
  Check
} from 'lucide-react';

const CRMLeadsManagement = () => {
  // React Query setup
  const queryClient = useQueryClient();
  
  // State management
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedLead, setSelectedLead] = useState(null);
  const [showLeadModal, setShowLeadModal] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(null);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, right: 0 });
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successData, setSuccessData] = useState(null);
  const [showCreateLeadModal, setShowCreateLeadModal] = useState(false);
  const [showConvertModal, setShowConvertModal] = useState(false);
  const [leadToConvert, setLeadToConvert] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [leadToDelete, setLeadToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [errorBanner, setErrorBanner] = useState(null);
  // NOTE: Lead Intake URL state removed - integration panel hidden from agents
  // Integration is configured internally by ParkPro team, not self-serve for agents
  // const [leadIntakeUrl, setLeadIntakeUrl] = useState(null);
  // const [isLoadingIntakeUrl, setIsLoadingIntakeUrl] = useState(false);
  // const [isUrlCopied, setIsUrlCopied] = useState(false);
  
  // Lead Page URL state (for Solo+ and Enterprise agents)
  const [leadPageUrl, setLeadPageUrl] = useState(null);
  const [isLeadPageUrlCopied, setIsLeadPageUrlCopied] = useState(false);
  
  const location = useLocation();

  const { user } = useUser();
  
  // Check if user has lead page feature (Agent+ or Enterprise)
  const hasLeadPageFeature = useMemo(() => {
    if (!user?.planId) return false;
    const planId = user.planId.toLowerCase().replace(/[_-]/g, '');
    return planId === 'agentplus' || planId === 'solo_plus' || planId === 'enterprise';
  }, [user?.planId]);
  
  // Build lead page URL
  useEffect(() => {
    if (hasLeadPageFeature && user) {
      // Use publicslug if available, otherwise fallback to userid
      const agentKey = user.publicslug || user.userid || user.username;
      if (agentKey) {
        const baseUrl = window.location.origin;
        const leadPageUrl = `${baseUrl}/lead/${agentKey}`;
        setLeadPageUrl(leadPageUrl);
      }
    }
  }, [hasLeadPageFeature, user]);
  
  // Copy lead page URL handler
  const handleCopyLeadPageUrl = async () => {
    if (!leadPageUrl) return;
    
    try {
      await navigator.clipboard.writeText(leadPageUrl);
      setIsLeadPageUrlCopied(true);
      toast.success('Lead page URL copied to clipboard!');
      
      setTimeout(() => {
        setIsLeadPageUrlCopied(false);
      }, 2000);
    } catch (error) {
      console.error('Failed to copy URL:', error);
      toast.error('Failed to copy URL to clipboard');
    }
  };

  // Debounced search effect
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Load real leads for logged-in agent
  useEffect(() => {
    // Scroll to top on navigation for consistency with other CRM pages
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [location.pathname]);

  // NOTE: Lead Intake URL fetching removed - integration panel hidden from agents
  // Integration is configured internally by ParkPro team, not self-serve for agents
  // The backend endpoint /api/crm/lead-intake-url remains available for future admin use
  // useEffect(() => {
  //   const fetchLeadIntakeUrl = async () => {
  //     if (!user?.userid) return;
  //     
  //     setIsLoadingIntakeUrl(true);
  //     try {
  //       const response = await fetch(`${import.meta.env.VITE_API_URL}/api/crm/lead-intake-url`, {
  //         method: 'GET',
  //         credentials: 'include',
  //         headers: {
  //           'Content-Type': 'application/json',
  //         },
  //       });
  //
  //       if (!response.ok) {
  //         throw new Error('Failed to fetch lead intake URL');
  //       }
  //
  //       const data = await response.json();
  //       if (data.success && data.url) {
  //         setLeadIntakeUrl(data.url);
  //       }
  //     } catch (error) {
  //       console.error('Error fetching lead intake URL:', error);
  //     } finally {
  //       setIsLoadingIntakeUrl(false);
  //     }
  //   };
  //
  //   fetchLeadIntakeUrl();
  // }, [user?.userid]);

  // NOTE: Copy URL handler removed - integration panel hidden from agents
  // const handleCopyUrl = async () => {
  //   if (!leadIntakeUrl) return;
  //
  //   try {
  //     await navigator.clipboard.writeText(leadIntakeUrl);
  //     setIsUrlCopied(true);
  //     toast.success('Lead intake URL copied to clipboard!');
  //     
  //     setTimeout(() => {
  //       setIsUrlCopied(false);
  //     }, 2000);
  //   } catch (error) {
  //     console.error('Failed to copy URL:', error);
  //     toast.error('Failed to copy URL to clipboard');
  //   }
  // };

  // Close dropdown when clicking outside or pressing Escape
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownOpen && !event.target.closest('.relative') && !event.target.closest('[data-dropdown-menu]')) {
        setDropdownOpen(null);
      }
    };

    const handleKeyDown = (event) => {
      if (event.key === 'Escape' && dropdownOpen) {
        setDropdownOpen(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown);
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [dropdownOpen]);

  // React Query hooks
  const {
    data: rawLeads = [],
    isLoading,
    error: queryError,
    refetch
  } = useQuery({
    queryKey: ['leads', user?.userid],
    queryFn: async () => {
      if (!user?.userid) return [];
      
      const response = await fetch(`${import.meta.env.VITE_API_URL}/api/crm/leads?t=${Date.now()}`, {
        credentials: 'include',
        cache: 'no-store'
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch leads');
      }
      
      const json = await response.json();
      return json.data || [];
    },
    enabled: !!user?.userid,
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: false,
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });

  // Normalize leads data
  const leads = useMemo(() => {
    return rawLeads.map(l => ({
      leadId: l.leadid,
      firstName: l.firstname,
      lastName: l.lastname,
      email: l.email,
      phone: l.phone,
      adults: l.adults || 0,
      children: l.children || 0,
      destination: l.destination,
      departureDate: l.departure_date,
      returnDate: l.return_date,
      source: l.source || 'widget',
      provider: l.provider || null,
      status: l.status || 'new',
      message: l.message,
      createdAt: l.createdat,
    }));
  }, [rawLeads]);

  // Scroll to top on navigation
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [location.pathname]);

  // Set error banner when query fails
  useEffect(() => {
    if (queryError) {
      setErrorBanner({
        message: queryError.message,
        onRetry: () => refetch()
      });
    } else {
      setErrorBanner(null);
    }
  }, [queryError, refetch]);

  // Filter and paginate leads
  const filteredLeads = useMemo(() => {
    return leads.filter(lead => {
      const matchesSearch = `${lead.firstName || ''} ${lead.lastName || ''}`.toLowerCase().includes(debouncedSearchTerm.toLowerCase()) ||
                           (lead.email || '').toLowerCase().includes(debouncedSearchTerm.toLowerCase());
      const matchesStatus = statusFilter === 'all' || lead.status === statusFilter;
      return matchesSearch && matchesStatus;
    });
  }, [leads, debouncedSearchTerm, statusFilter]);

  // Paginated leads
  const paginatedLeads = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return filteredLeads.slice(startIndex, startIndex + itemsPerPage);
  }, [filteredLeads, currentPage, itemsPerPage]);

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearchTerm, statusFilter]);

  // Simple analytics similar to Trip/Client pages
  const analytics = {
    totalLeads: leads.length,
    newLeads: leads.filter(l => l.status === 'new').length,
    contactedLeads: leads.filter(l => l.status === 'contacted').length,
    qualifiedLeads: leads.filter(l => l.status === 'qualified').length,
    convertedLeads: leads.filter(l => l.status === 'converted').length,
    lostLeads: leads.filter(l => l.status === 'lost').length,
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'new': return 'bg-gray-100 text-gray-800 border-gray-200';
      case 'contacted': return 'bg-amber-100 text-amber-800 border-amber-200';
      case 'qualified': return 'bg-indigo-100 text-indigo-800 border-indigo-200';
      case 'converted': return 'bg-green-100 text-green-800 border-green-200';
      case 'lost': return 'bg-rose-100 text-rose-800 border-rose-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  // React Query mutations
  const updateStatusMutation = useMutation({
    mutationFn: async ({ leadId, status }) => {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/api/crm/leads/${leadId}/status`, {
        method: 'PUT',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ status }),
      });
      
      if (!response.ok) {
        throw new Error('Failed to update lead status');
      }
      
      return response.json();
    },
    onMutate: async ({ leadId, status }) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: ['leads', user?.userid] });
      
      // Snapshot previous value
      const previousLeads = queryClient.getQueryData(['leads', user?.userid]);
      
      // Optimistically update
      queryClient.setQueryData(['leads', user?.userid], (old) => {
        if (!old) return old;
        return old.map(lead => 
          lead.leadid === leadId ? { ...lead, status } : lead
        );
      });
      
      return { previousLeads };
    },
    onError: (err, variables, context) => {
      // Rollback on error
      if (context?.previousLeads) {
        queryClient.setQueryData(['leads', user?.userid], context.previousLeads);
      }
      toast.error('Failed to update lead status');
    },
    onSuccess: () => {
      toast.success('Lead status updated successfully');
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['leads', user?.userid] });
    },
  });

  const deleteLeadMutation = useMutation({
    mutationFn: async (leadId) => {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/api/crm/leads/${leadId}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      
      if (!response.ok) {
        throw new Error('Failed to delete lead');
      }
      
      return response.json();
    },
    onSuccess: () => {
      toast.success('Lead deleted successfully');
      queryClient.invalidateQueries({ queryKey: ['leads', user?.userid] });
    },
    onError: () => {
      toast.error('Failed to delete lead');
    },
  });

  // Helper function for avatar initials with null safety
  const getInitials = (firstName, lastName) => {
    const first = firstName || '';
    const last = lastName || '';
    return `${first.charAt(0)}${last.charAt(0)}`.toUpperCase() || '?';
  };

  // Helper function to get client initials from full name
  const getClientInitials = (fullName) => {
    if (!fullName) return "C";
    const names = fullName.trim().split(" ");
    if (names.length >= 2) {
      return (names[0].charAt(0) + names[names.length - 1].charAt(0)).toUpperCase();
    }
    return fullName.charAt(0).toUpperCase();
  };

  const getDestinationName = (destination) => {
    switch (destination) {
      case 'disney_world': return 'Walt Disney World';
      case 'disneyland': return 'Disneyland';
      case 'universal_orlando': return 'Universal Orlando';
      case 'disney_cruise': return 'Disney Cruise Line';
      default: return destination;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'N/A';
    
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatPhoneNumber = (phone) => {
    if (!phone) return 'N/A';
    
    // Remove all non-digit characters
    const cleaned = phone.replace(/\D/g, '');
    
    // Check if it's a valid 10-digit US phone number
    if (cleaned.length === 10) {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
    }
    
    // If it's 11 digits and starts with 1, remove the 1
    if (cleaned.length === 11 && cleaned.startsWith('1')) {
      const withoutOne = cleaned.slice(1);
      return `(${withoutOne.slice(0, 3)}) ${withoutOne.slice(3, 6)}-${withoutOne.slice(6)}`;
    }
    
    // Return original if it doesn't match expected format
    return phone;
  };

  const handleStatusUpdate = (leadId, newStatus) => {
    updateStatusMutation.mutate({ leadId, status: newStatus });
  };

  const handleConvertLead = (lead) => {
    setLeadToConvert(lead);
    setShowConvertModal(true);
    setDropdownOpen(null);
  };

  const handleConversionSuccess = (result) => {
    // Update local state to show lead as converted
    setLeads(prev => prev.map(lead => 
      lead.leadId === result.leadId ? { ...lead, status: 'converted' } : lead
    ));
    
    toast.success('Lead converted successfully!');
    setShowConvertModal(false);
    setLeadToConvert(null);
  };

  const handleMarkAsLost = async (leadId) => {
    try {
      await handleStatusUpdate(leadId, 'lost');
    } catch (err) {
      console.error('Failed to mark lead as lost:', err);
    }
  };

  const handleDeleteLead = (leadId) => {
    deleteLeadMutation.mutate(leadId, {
      onSuccess: () => {
        setShowDeleteModal(false);
        setLeadToDelete(null);
      }
    });
  };

  const openDeleteModal = (lead) => {
    setLeadToDelete(lead);
    setShowDeleteModal(true);
    setDropdownOpen(null);
  };

  const handleExportLead = (lead) => {
    const dataStr = JSON.stringify(lead, null, 2);
    const dataUri = 'data:application/json;charset=utf-8,'+ encodeURIComponent(dataStr);
    
    const exportFileDefaultName = `lead-${lead.firstName}-${lead.lastName}-${lead.leadId}.json`;
    
    const linkElement = document.createElement('a');
    linkElement.setAttribute('href', dataUri);
    linkElement.setAttribute('download', exportFileDefaultName);
    linkElement.click();
  };

  const handleLeadCreated = (newLead) => {
    // Convert the backend lead format to frontend format
    const normalizedLead = {
      leadId: newLead.leadid,
      firstName: newLead.firstname,
      lastName: newLead.lastname,
      email: newLead.email,
      phone: newLead.phone,
      adults: newLead.adults || 0,
      children: newLead.children || 0,
      destination: newLead.destination,
      departureDate: newLead.departure_date,
      returnDate: newLead.return_date,
      source: newLead.source || 'manual',
      status: newLead.status || 'new',
      message: newLead.message,
      createdAt: newLead.createdat,
    };

    // Add to the leads list
    setLeads(prev => [normalizedLead, ...prev]);
    setShowCreateLeadModal(false);
  };

  return (
    <div className="max-w-[1400px] mx-auto">
            {/* Error Banner */}
            {errorBanner && (
              <div className="p-4 mb-4 bg-red-50 rounded-lg border border-red-200">
                <div className="flex gap-3 items-start">
                  <AlertCircle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
                  <div className="flex-1">
                    <h3 className="text-sm font-medium text-red-800">Error loading leads</h3>
                    <p className="mt-1 text-sm text-red-700">{errorBanner.message}</p>
                    <button
                      onClick={errorBanner.onRetry}
                      className="mt-2 text-sm font-medium text-red-800 underline hover:text-red-900"
                    >
                      Try again
                    </button>
                  </div>
                  <button
                    onClick={() => setErrorBanner(null)}
                    className="text-red-400 hover:text-red-600"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}

            {/* Header */}
            <div className="flex flex-col gap-4 justify-between mb-4 md:flex-row md:items-center animate-fade-in-up">
              <div>
                <h1 className="text-xl font-bold tracking-tight leading-tight text-black sm:text-2xl lg:text-3xl text-balance">
                  Lead Management
                </h1>
                <p className="mt-1 text-sm text-gray-600">
                  Manage and track incoming leads
                </p>
              </div>
              <div className="flex gap-3 items-center">
                <button 
                  onClick={() => setShowCreateLeadModal(true)}
                  className="inline-flex gap-2 items-center px-4 py-2 text-sm font-semibold text-white bg-blue-600 rounded-lg shadow transition-all duration-200 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-gray-400"
                >
                  <Plus className="w-4 h-4" />
                  Add Lead
                </button>
              </div>
            </div>

            {/* Your ParkPro Lead Page - Solo+ and Enterprise only */}
            {hasLeadPageFeature && leadPageUrl && (
              <div className="p-4 mb-4 bg-gradient-to-r from-purple-50 to-pink-50 rounded-lg border border-purple-200 shadow-sm animate-fade-in-up">
                <div className="flex flex-col gap-3">
                  <div className="flex gap-3 items-start">
                    <div className="flex-shrink-0 p-2 bg-purple-100 rounded-lg">
                      <Link className="w-5 h-5 text-purple-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-base font-semibold text-gray-900">
                        Your ParkPro Lead Page
                      </h3>
                      <p className="mt-1 text-sm text-gray-600">
                        This is your personal ParkPro lead form link. Share it on your website, social profiles, or email signature so new clients can request a quote.
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex flex-col gap-2 mt-2 sm:flex-row">
                    <div className="flex flex-1 gap-2 items-center p-3 bg-white rounded-lg border border-gray-200">
                      <input
                        type="text"
                        value={leadPageUrl}
                        readOnly
                        className="flex-1 text-sm text-gray-900 bg-transparent border-0 focus:outline-none focus:ring-0"
                        placeholder="Loading lead page URL..."
                      />
                      <button
                        onClick={handleCopyLeadPageUrl}
                        disabled={!leadPageUrl}
                        className="flex-shrink-0 p-2 text-gray-600 bg-gray-100 rounded-lg transition-colors hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
                        title="Copy URL"
                      >
                        {isLeadPageUrlCopied ? (
                          <Check className="w-4 h-4 text-green-600" />
                        ) : (
                          <Copy className="w-4 h-4" />
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* NOTE: Website & Enterprise Lead Integration Panel - Hidden from agents
                Integration is configured internally by ParkPro team, not self-serve for agents.
                The backend API endpoint remains fully functional for internal/ops use.
                To re-enable: uncomment the panel JSX below and restore the related state/useEffect above.
            */}
            {/* <div className="p-4 mb-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border border-blue-200 shadow-sm animate-fade-in-up">
              <div className="flex flex-col gap-3">
                <div className="flex gap-3 items-start">
                  <div className="flex-shrink-0 p-2 bg-blue-100 rounded-lg">
                    <Link className="w-5 h-5 text-blue-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-base font-semibold text-gray-900">
                      Website & Enterprise Lead Integration
                    </h3>
                    <p className="mt-1 text-sm text-gray-600">
                      Connect your existing website or CRM to ParkPro. Use this URL as a webhook or API endpoint to automatically send new leads directly into your Lead Management dashboard.
                    </p>
                  </div>
                </div>
                
                <div className="flex flex-col gap-2 mt-2 sm:flex-row">
                  <div className="flex flex-1 gap-2 items-center p-3 bg-white rounded-lg border border-gray-200">
                    <input
                      type="text"
                      value={leadIntakeUrl || (isLoadingIntakeUrl ? 'Loading...' : '')}
                      readOnly
                      className="flex-1 text-sm text-gray-900 bg-transparent border-0 focus:outline-none focus:ring-0"
                      placeholder="Loading lead intake URL..."
                    />
                    <button
                      onClick={handleCopyUrl}
                      disabled={!leadIntakeUrl || isLoadingIntakeUrl}
                      className="flex-shrink-0 p-2 text-gray-600 bg-gray-100 rounded-lg transition-colors hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
                      title="Copy URL"
                    >
                      {isUrlCopied ? (
                        <Check className="w-4 h-4 text-green-600" />
                      ) : (
                        <Copy className="w-4 h-4" />
                      )}
                    </button>
                  </div>
                </div>

                <div className="flex flex-col gap-1 mt-1 text-xs text-gray-500">
                  <p>
                    This works with tools like <strong>JotForm</strong>, <strong>Typeform</strong>, <strong>Webflow</strong>, <strong>Travefy</strong>, or custom in-house systems.
                  </p>
                  <p>
                    For Enterprise agencies, share this URL and our Developer Docs with your tech team.
                  </p>
                  <p className="mt-1 text-xs text-gray-400">
                    Tip: You can append <code className="px-1 py-0.5 bg-gray-100 rounded">&provider=jotform</code>, <code className="px-1 py-0.5 bg-gray-100 rounded">&provider=travefy</code>, or <code className="px-1 py-0.5 bg-gray-100 rounded">&provider=custom_crm</code> to help ParkPro tag and analyze your lead sources.
                  </p>
                </div>
              </div>
            </div> */}

            {/* Analytics */}
            <div className="grid grid-cols-2 gap-3 mb-4 sm:grid-cols-3 md:grid-cols-6 lg:grid-cols-6 animate-fade-in-up sm:gap-4">
              <div className="p-3 bg-white rounded-lg border border-gray-200 shadow-sm sm:p-4">
                <div className="flex justify-between items-center">
                  <div>
                    <p className="text-xs font-medium text-gray-600 sm:text-sm">Total Leads</p>
                    <p className="text-xl font-bold text-black sm:text-2xl">{analytics.totalLeads}</p>
                  </div>
                  <Users className="w-6 h-6 text-gray-700 sm:w-8 sm:h-8" />
                </div>
              </div>
              <div className="p-3 bg-white rounded-lg border border-gray-200 shadow-sm sm:p-4">
                <div className="flex justify-between items-center">
                  <div>
                    <p className="text-xs font-medium text-gray-600 sm:text-sm">New</p>
                    <p className="text-xl font-bold text-black sm:text-2xl">{analytics.newLeads}</p>
                  </div>
                  <RefreshCw className="w-6 h-6 text-gray-700 sm:w-8 sm:h-8" />
                </div>
              </div>
              <div className="p-3 bg-white rounded-lg border border-gray-200 shadow-sm sm:p-4">
                <div className="flex justify-between items-center">
                  <div>
                    <p className="text-xs font-medium text-gray-600 sm:text-sm">Contacted</p>
                    <p className="text-xl font-bold text-black sm:text-2xl">{analytics.contactedLeads}</p>
                  </div>
                  <UserCheck className="w-6 h-6 text-gray-700 sm:w-8 sm:h-8" />
                </div>
              </div>
              <div className="p-3 bg-white rounded-lg border border-gray-200 shadow-sm sm:p-4">
                <div className="flex justify-between items-center">
                  <div>
                    <p className="text-xs font-medium text-gray-600 sm:text-sm">Qualified</p>
                    <p className="text-xl font-bold text-black sm:text-2xl">{analytics.qualifiedLeads}</p>
                  </div>
                  <UserCheck className="w-6 h-6 text-gray-700 sm:w-8 sm:h-8" />
                </div>
              </div>
              <div className="p-3 bg-white rounded-lg border border-gray-200 shadow-sm sm:p-4">
                <div className="flex justify-between items-center">
                  <div>
                    <p className="text-xs font-medium text-gray-600 sm:text-sm">Converted</p>
                    <p className="text-xl font-bold text-black sm:text-2xl">{analytics.convertedLeads}</p>
                  </div>
                  <UserCheck className="w-6 h-6 text-gray-700 sm:w-8 sm:h-8" />
                </div>
              </div>
              <div className="p-3 bg-white rounded-lg border border-gray-200 shadow-sm sm:p-4">
                <div className="flex justify-between items-center">
                  <div>
                    <p className="text-xs font-medium text-gray-600 sm:text-sm">Lost</p>
                    <p className="text-xl font-bold text-black sm:text-2xl">{analytics.lostLeads}</p>
                  </div>
                  <UserX className="w-6 h-6 text-gray-700 sm:w-8 sm:h-8" />
                </div>
              </div>
            </div>

            {/* Filters */}
            <div className="flex gap-4 items-center mb-4 animate-fade-in-up">
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 w-4 h-4 text-gray-400 -translate-y-1/2" />
                <input
                  type="text"
                  placeholder="Search leads..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="py-2 pr-4 pl-10 w-full rounded-lg border border-gray-300 placeholder:text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div className="relative">
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="py-2 pr-8 pl-3 text-sm text-gray-700 bg-white rounded-lg border border-gray-300 shadow-sm transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 hover:border-gray-400"
                  style={{ appearance: 'none', WebkitAppearance: 'none', MozAppearance: 'none' }}
                >
                  <option value="all">All Status</option>
                  <option value="new">New</option>
                  <option value="contacted">Contacted</option>
                  <option value="qualified">Qualified</option>
                  <option value="converted">Converted</option>
                  <option value="lost">Lost</option>
                </select>
                <ChevronDown className="absolute right-2 top-1/2 w-4 h-4 text-gray-400 -translate-y-1/2 pointer-events-none" />
              </div>
              
              <button
                className={`crm-filter-btn ${false ? 'active' : ''}`}
                onClick={() => {/* TODO: Add filters functionality */}}
              >
                <Filter size={18} />
                <span>Filters</span>
                <ChevronDown size={16} />
              </button>
            </div>

            {/* Leads List */}
            {isLoading ? (
              <div className="flex justify-center py-12 bg-white rounded-xl border border-gray-200 shadow-sm">
                <div className="flex gap-2 items-center">
                  <div className="w-6 h-6 rounded-full border-2 border-black animate-spin border-t-transparent"></div>
                  <span className="text-sm text-gray-600">Loading leads...</span>
                </div>
              </div>
            ) : (
              <>
                {/* Desktop Table View */}
                <div className="hidden bg-white rounded-lg border border-gray-200 shadow-sm lg:block">
                  <div className="overflow-x-auto overflow-y-visible">
                    <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">
                  Lead
                </th>
                <th className="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">
                  Contact
                </th>
                <th className="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">
                  Trip Details
                </th>
                <th className="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">
                  Status
                </th>
                <th className="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">
                  Source
                </th>
                <th className="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {isLoading ? (
                // Loading skeleton
                Array.from({ length: 5 }).map((_, index) => (
                  <tr key={index} className="animate-pulse">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="w-10 h-10 bg-gray-200 rounded-full"></div>
                        <div className="ml-4">
                          <div className="w-24 h-4 bg-gray-200 rounded"></div>
                          <div className="mt-1 w-32 h-3 bg-gray-200 rounded"></div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="w-20 h-4 bg-gray-200 rounded"></div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="w-16 h-4 bg-gray-200 rounded"></div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="w-12 h-6 bg-gray-200 rounded-full"></div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="w-20 h-4 bg-gray-200 rounded"></div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="w-8 h-8 bg-gray-200 rounded"></div>
                    </td>
                  </tr>
                ))
              ) : paginatedLeads.length === 0 ? (
                // Empty state
                <tr>
                  <td colSpan="6" className="px-6 py-12 text-center">
                    <div className="flex flex-col gap-4 items-center">
                      <div className="flex justify-center items-center w-16 h-16 bg-gray-100 rounded-full">
                        <Users className="w-8 h-8 text-gray-400" />
                      </div>
                      <div>
                        <h3 className="mb-1 text-lg font-medium text-gray-900">
                          {filteredLeads.length === 0 && leads.length > 0 
                            ? 'No leads match your filters'
                            : 'No leads yet'
                          }
                        </h3>
                        <p className="mb-4 text-sm text-gray-500">
                          {filteredLeads.length === 0 && leads.length > 0
                            ? 'Try adjusting your search or filter criteria'
                            : 'Get started by adding your first lead'
                          }
                        </p>
                        <button
                          onClick={() => setShowCreateLeadModal(true)}
                          className="inline-flex gap-2 items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg transition-colors hover:bg-blue-700"
                        >
                          <Plus className="w-4 h-4" />
                          Add Lead
                        </button>
                      </div>
                    </div>
                  </td>
                </tr>
              ) : (
                paginatedLeads.map((lead) => (
                <motion.tr
                  key={lead.leadId}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="transition-colors hover:bg-gray-50"
                >
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 w-10 h-10">
                        <div className="flex justify-center items-center w-10 h-10 bg-gradient-to-br from-blue-400 to-blue-700 rounded-xl">
                          <span className="text-sm font-bold text-white">
                            {getInitials(lead.firstName, lead.lastName)}
                          </span>
                        </div>
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900">
                          {lead.firstName} {lead.lastName}
                        </div>
                        <div className="text-sm text-gray-500">
                          {formatDate(lead.createdAt)}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{lead.email}</div>
                    <div className="text-sm text-gray-500">{formatPhoneNumber(lead.phone)}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      {getDestinationName(lead.destination)}
                    </div>
                    <div className="text-sm text-gray-500">
                      {lead.adults} adults, {lead.children} children
                    </div>
                    {lead.departureDate && (
                      <div className="text-sm text-gray-500">
                        {formatDate(lead.departureDate)} - {formatDate(lead.returnDate)}
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(lead.status)}`}>
                      {lead.status.charAt(0).toUpperCase() + lead.status.slice(1)}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm whitespace-nowrap">
                    <div className="flex flex-col gap-1">
                      <span className="font-medium text-gray-900">
                        {lead.source === 'website_form' || lead.source === 'external_form' ? 'Website' : lead.source.charAt(0).toUpperCase() + lead.source.slice(1)}
                      </span>
                      {lead.provider && lead.provider !== 'generic' && (
                        <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium bg-blue-100 text-blue-800 rounded-full">
                          {lead.provider.charAt(0).toUpperCase() + lead.provider.slice(1).replace(/_/g, ' ')}
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm font-medium whitespace-nowrap">
                    <div className="flex justify-start items-center space-x-2">
                      <button
                        onClick={() => {
                          setSelectedLead(lead);
                          setShowLeadModal(true);
                        }}
                        className="relative flex items-center gap-1.5 px-2.5 py-1.5 text-blue-600 hover:text-blue-900 hover:bg-blue-50 rounded-md transition-colors group border border-blue-200 hover:border-blue-300"
                        title="View lead details"
                      >
                        <Eye className="w-4 h-4" />
                        <span className="text-xs font-medium">View</span>
                        <div className="absolute bottom-full left-1/2 z-10 px-2 py-1 mb-2 text-xs text-white whitespace-nowrap bg-gray-800 rounded opacity-0 transition-opacity duration-200 transform -translate-x-1/2 group-hover:opacity-100">
                          View lead details
                        </div>
                      </button>
                      
                      {lead.status === 'new' && (
                        <button
                          onClick={() => handleStatusUpdate(lead.leadId, 'contacted')}
                          className="relative flex items-center gap-1.5 px-2.5 py-1.5 text-yellow-700 hover:text-yellow-900 hover:bg-yellow-50 rounded-md transition-colors group border border-yellow-200 hover:border-yellow-300"
                          title="Mark as contacted"
                        >
                          <UserCheck className="w-4 h-4" />
                          <span className="text-xs font-medium">Contact</span>
                          <div className="absolute bottom-full left-1/2 z-10 px-2 py-1 mb-2 text-xs text-white whitespace-nowrap bg-gray-800 rounded opacity-0 transition-opacity duration-200 transform -translate-x-1/2 group-hover:opacity-100">
                            Mark as contacted
                          </div>
                        </button>
                      )}
                      
                      {lead.status === 'qualified' && (
                        <button
                          onClick={() => handleConvertLead(lead)}
                          className="relative flex items-center gap-1.5 px-2.5 py-1.5 text-green-700 hover:text-green-900 hover:bg-green-50 rounded-md transition-colors group border border-green-200 hover:border-green-300"
                          title="Convert & create trip"
                        >
                          <UserCheck className="w-4 h-4" />
                          <span className="text-xs font-medium">Convert & Create Trip</span>
                          <div className="absolute bottom-full left-1/2 z-10 px-2 py-1 mb-2 text-xs text-white whitespace-nowrap bg-gray-800 rounded opacity-0 transition-opacity duration-200 transform -translate-x-1/2 group-hover:opacity-100">
                            Convert to client & create trip
                          </div>
                        </button>
                      )}
                      
                      <div className="relative">
                        <button 
                          onClick={(e) => {
                            const rect = e.currentTarget.getBoundingClientRect();
                            setDropdownOpen(dropdownOpen === lead.leadId ? null : lead.leadId);
                            setDropdownPosition({
                              top: rect.bottom + window.scrollY,
                              right: window.innerWidth - rect.right - window.scrollX
                            });
                          }}
                          className={`relative flex items-center gap-1.5 px-2.5 py-1.5 rounded-md transition-colors group border ${
                            dropdownOpen === lead.leadId 
                              ? 'text-gray-700 bg-gray-100 border-gray-300' 
                              : 'text-gray-600 hover:text-gray-700 hover:bg-gray-50 border-gray-200 hover:border-gray-300'
                          }`}
                          title="More actions"
                        >
                          <MoreVertical className="w-4 h-4" />
                          <div className="absolute bottom-full left-1/2 z-10 px-2 py-1 mb-2 text-xs text-white whitespace-nowrap bg-gray-800 rounded opacity-0 transition-opacity duration-200 transform -translate-x-1/2 group-hover:opacity-100">
                            More actions
                          </div>
                        </button>
                      </div>
                    </div>
                  </td>
                </motion.tr>
                ))
              )}
            </tbody>
                  </table>
                </div>
                </div>

                {/* Mobile Card View */}
                <div className="space-y-3 lg:hidden">
                  {isLoading ? (
                    // Mobile loading skeleton
                    Array.from({ length: 3 }).map((_, index) => (
                      <div key={index} className="p-4 bg-white rounded-lg border border-gray-200 animate-pulse">
                        <div className="flex gap-3 items-center">
                          <div className="w-12 h-12 bg-gray-200 rounded-full"></div>
                          <div className="flex-1">
                            <div className="mb-2 w-32 h-4 bg-gray-200 rounded"></div>
                            <div className="w-24 h-3 bg-gray-200 rounded"></div>
                          </div>
                          <div className="w-16 h-6 bg-gray-200 rounded-full"></div>
                        </div>
                      </div>
                    ))
                  ) : paginatedLeads.length === 0 ? (
                    // Mobile empty state
                    <div className="p-8 text-center bg-white rounded-lg border border-gray-200">
                      <div className="flex justify-center items-center mx-auto mb-4 w-16 h-16 bg-gray-100 rounded-full">
                        <Users className="w-8 h-8 text-gray-400" />
                      </div>
                      <h3 className="mb-1 text-lg font-medium text-gray-900">
                        {filteredLeads.length === 0 && leads.length > 0 
                          ? 'No leads match your filters'
                          : 'No leads yet'
                        }
                      </h3>
                      <p className="mb-4 text-sm text-gray-500">
                        {filteredLeads.length === 0 && leads.length > 0
                          ? 'Try adjusting your search or filter criteria'
                          : 'Get started by adding your first lead'
                        }
                      </p>
                      <button
                        onClick={() => setShowCreateLeadModal(true)}
                        className="inline-flex gap-2 items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg transition-colors hover:bg-blue-700"
                      >
                        <Plus className="w-4 h-4" />
                        Add Lead
                      </button>
                    </div>
                  ) : (
                    paginatedLeads.map((lead) => (
                      <motion.div
                        key={lead.leadId}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="p-4 bg-white rounded-lg border border-gray-200 shadow-sm"
                      >
                        <div className="flex justify-between items-start mb-3">
                          <div className="flex gap-3 items-center">
                            <div className="flex justify-center items-center w-12 h-12 bg-gradient-to-br from-blue-400 to-blue-700 rounded-xl">
                              <span className="text-sm font-bold text-white">
                                {getInitials(lead.firstName, lead.lastName)}
                              </span>
                            </div>
                            <div>
                              <h3 className="font-medium text-gray-900">
                                {lead.firstName} {lead.lastName}
                              </h3>
                              <p className="text-sm text-gray-500">{lead.email}</p>
                              <p className="text-xs text-gray-400">{formatDate(lead.createdAt)}</p>
                            </div>
                          </div>
                          <div className="flex gap-2 items-center">
                            <span className={`px-2 py-1 text-xs font-medium rounded-full border ${getStatusColor(lead.status)}`}>
                              {lead.status}
                            </span>
                            <div className="relative">
                              <button
                                onClick={() => setDropdownOpen(dropdownOpen === lead.leadId ? null : lead.leadId)}
                                className="p-1 text-gray-400 rounded hover:text-gray-600"
                                aria-label="More actions"
                                aria-expanded={dropdownOpen === lead.leadId}
                                aria-haspopup="menu"
                              >
                                <MoreVertical className="w-4 h-4" />
                              </button>
                              {dropdownOpen === lead.leadId && (
                                <div 
                                  className="absolute right-0 z-10 py-1 mt-1 w-48 bg-white rounded-md border border-gray-200 shadow-lg"
                                  role="menu"
                                  aria-orientation="vertical"
                                  data-dropdown-menu
                                >
                                  <button
                                    onClick={() => {
                                      setSelectedLead(lead);
                                      setShowLeadModal(true);
                                      setDropdownOpen(null);
                                    }}
                                    className="flex items-center px-4 py-2.5 w-full text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                                  >
                                    <Eye className="mr-3 w-4 h-4" />
                                    <span className="font-medium">View Details</span>
                                  </button>
                                  {lead.status === 'new' && (
                                    <button
                                      onClick={() => {
                                        handleStatusUpdate(lead.leadId, 'contacted');
                                        setDropdownOpen(null);
                                      }}
                                      className="flex items-center px-4 py-2.5 w-full text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                                    >
                                      <UserCheck className="mr-3 w-4 h-4" />
                                      <span className="font-medium">Mark as Contacted</span>
                                    </button>
                                  )}
                                  {lead.status === 'contacted' && (
                                    <button
                                      onClick={() => {
                                        handleStatusUpdate(lead.leadId, 'qualified');
                                        setDropdownOpen(null);
                                      }}
                                      className="flex items-center px-4 py-2.5 w-full text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                                    >
                                      <UserCheck className="mr-3 w-4 h-4" />
                                      <span className="font-medium">Mark as Qualified</span>
                                    </button>
                                  )}
                                  {lead.status === 'qualified' && (
                                    <button
                                      onClick={() => {
                                        handleConvertLead(lead);
                                        setDropdownOpen(null);
                                      }}
                                      className="flex items-center px-4 py-2.5 w-full text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                                    >
                                      <UserCheck className="mr-3 w-4 h-4" />
                                      <span className="font-medium">Convert to Client</span>
                                    </button>
                                  )}
                                  {lead.status !== 'lost' && (
                                    <button
                                      onClick={() => {
                                        handleMarkAsLost(lead.leadId);
                                        setDropdownOpen(null);
                                      }}
                                      className="flex items-center px-4 py-2.5 w-full text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                                    >
                                      <UserX className="mr-3 w-4 h-4" />
                                      <span className="font-medium">Mark as Lost</span>
                                    </button>
                                  )}
                                  <button
                                    onClick={() => {
                                      openDeleteModal(lead);
                                    }}
                                    className="flex items-center px-4 py-2.5 w-full text-sm text-red-600 hover:bg-red-50 transition-colors"
                                  >
                                    <UserX className="mr-3 w-4 h-4" />
                                    <span className="font-medium">Delete</span>
                                  </button>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-3 text-sm">
                          <div>
                            <span className="text-gray-500">Destination:</span>
                            <p className="font-medium">{getDestinationName(lead.destination)}</p>
                          </div>
                          <div>
                            <span className="text-gray-500">Travel Date:</span>
                            <p className="font-medium">{formatDate(lead.departureDate)}</p>
                          </div>
                          <div>
                            <span className="text-gray-500">Phone:</span>
                            <p className="font-medium">{formatPhoneNumber(lead.phone)}</p>
                          </div>
                          <div>
                            <span className="text-gray-500">Source:</span>
                            <div className="flex flex-col gap-1">
                              <p className="font-medium capitalize">
                                {lead.source === 'website_form' || lead.source === 'external_form' ? 'Website' : lead.source}
                              </p>
                              {lead.provider && lead.provider !== 'generic' && (
                                <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium bg-blue-100 text-blue-800 rounded-full w-fit">
                                  {lead.provider.charAt(0).toUpperCase() + lead.provider.slice(1).replace(/_/g, ' ')}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </motion.div>
                    ))
                  )}
                </div>
              </>
            )}

            {/* Pagination */}
            {filteredLeads.length > itemsPerPage && (
              <div className="flex justify-between items-center px-4 mt-6 sm:px-6">
                <div className="flex items-center text-sm text-gray-700">
                  <span>
                    Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, filteredLeads.length)} of {filteredLeads.length} results
                  </span>
                </div>
                <div className="flex gap-2 items-center">
                  <button
                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                    disabled={currentPage === 1}
                    className="px-3 py-2 text-sm font-medium text-gray-500 bg-white rounded-md border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Previous
                  </button>
                  <div className="flex gap-1 items-center">
                    {Array.from({ length: Math.ceil(filteredLeads.length / itemsPerPage) }, (_, i) => i + 1)
                      .filter(page => {
                        const totalPages = Math.ceil(filteredLeads.length / itemsPerPage);
                        return page === 1 || page === totalPages || Math.abs(page - currentPage) <= 1;
                      })
                      .map((page, index, array) => (
                        <React.Fragment key={page}>
                          {index > 0 && array[index - 1] !== page - 1 && (
                            <span className="px-2 text-gray-500">...</span>
                          )}
                          <button
                            onClick={() => setCurrentPage(page)}
                            className={`px-3 py-2 text-sm font-medium rounded-md ${
                              page === currentPage
                                ? 'text-blue-600 bg-blue-50 border border-blue-300'
                                : 'text-gray-500 bg-white border border-gray-300 hover:bg-gray-50'
                            }`}
                          >
                            {page}
                          </button>
                        </React.Fragment>
                      ))}
                  </div>
                  <button
                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, Math.ceil(filteredLeads.length / itemsPerPage)))}
                    disabled={currentPage === Math.ceil(filteredLeads.length / itemsPerPage)}
                    className="px-3 py-2 text-sm font-medium text-gray-500 bg-white rounded-md border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}

            {/* Lead Detail Modal */}
      {showLeadModal && selectedLead && (
        <div className="flex fixed inset-0 z-50 justify-center items-center bg-black bg-opacity-50">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto"
          >
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900">
                Lead Details
              </h3>
              <button
                onClick={() => setShowLeadModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                ×
              </button>
            </div>
            
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Name</label>
                  <p className="text-sm text-gray-900">{selectedLead.firstName} {selectedLead.lastName}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Status</label>
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(selectedLead.status)}`}>
                    {selectedLead.status.charAt(0).toUpperCase() + selectedLead.status.slice(1)}
                  </span>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Email</label>
                  <p className="text-sm text-gray-900">{selectedLead.email}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Phone</label>
                  <p className="text-sm text-gray-900">{selectedLead.phone}</p>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Destination</label>
                  <p className="text-sm text-gray-900">{getDestinationName(selectedLead.destination)}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Travel Party</label>
                  <p className="text-sm text-gray-900">{selectedLead.adults} adults, {selectedLead.children} children</p>
                </div>
              </div>
              
              {selectedLead.departureDate && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Departure Date</label>
                    <p className="text-sm text-gray-900">{formatDate(selectedLead.departureDate)}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Return Date</label>
                    <p className="text-sm text-gray-900">{formatDate(selectedLead.returnDate)}</p>
                  </div>
                </div>
              )}
              
              {selectedLead.message && (
                <div>
                  <label className="block text-sm font-medium text-gray-700">Message</label>
                  <p className="text-sm text-gray-900">{selectedLead.message}</p>
                </div>
              )}
            </div>
            
            <div className="flex justify-end items-center pt-4 mt-6 space-x-3 border-t border-gray-200">
              <button
                onClick={() => setShowLeadModal(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg transition-colors hover:bg-gray-200"
              >
                Close
              </button>
              {selectedLead.status !== 'converted' && (
                <button
                  onClick={() => {
                    handleConvertLead(selectedLead);
                    setShowLeadModal(false);
                  }}
                  className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-lg transition-colors hover:bg-green-700"
                >
                  Convert & Create Trip
                </button>
              )}
            </div>
          </motion.div>
        </div>
      )}

      {/* Success Modal */}
      {showSuccessModal && successData && (
        <div className="flex fixed inset-0 z-[9999] justify-center items-center bg-black bg-opacity-75">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="relative z-[10000] p-8 mx-4 w-full max-w-md text-center bg-white rounded-2xl shadow-2xl transition-all duration-300 transform scale-100"
          >
            <div className="flex flex-col items-center">
              {/* Success Icon */}
              <div className="flex justify-center items-center mb-6 w-24 h-24 bg-gradient-to-br from-green-100 to-green-200 rounded-full shadow-lg">
                <svg className="w-14 h-14 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                </svg>
              </div>

              {/* Success Message */}
              <h2 className="mb-4 text-4xl font-bold text-green-600">Success!</h2>
              <p className="mb-4 text-lg leading-relaxed text-center text-gray-700">
                Lead successfully converted to client!
              </p>
              <p className="mb-6 text-sm leading-relaxed text-center text-gray-600">
                <strong>{successData.leadName}</strong> is now a client in your CRM system.
              </p>

              {/* Client Info */}
              <div className="p-4 mb-6 w-full bg-gray-50 rounded-lg border border-gray-200">
                <div className="text-sm text-gray-600">
                  <div className="flex justify-between items-center mb-2">
                    <span className="font-medium">Client ID:</span>
                    <span className="px-2 py-1 font-mono text-xs bg-white rounded border">{successData.clientId}</span>
                  </div>
                </div>
              </div>

              {/* Next Steps */}
              <div className="flex flex-col items-center mb-6 space-y-3 text-sm text-gray-500">
                <div className="flex items-center space-x-2">
                  <span className="text-blue-500">📋</span>
                  <span>Client appears in CRM Client Management</span>
                </div>
                <div className="flex items-center space-x-2">
                  <span className="text-yellow-500">📝</span>
                  <span>Trip will be created when questionnaire is completed</span>
                </div>
                <div className="flex items-center space-x-2">
                  <span className="text-green-500">🎯</span>
                  <span>Lead status updated to "Converted"</span>
                </div>
              </div>

              {/* Close Button */}
              <button
                onClick={() => setShowSuccessModal(false)}
                className="px-6 py-3 text-sm font-medium text-white bg-blue-600 rounded-lg transition-all duration-200 hover:bg-blue-700 hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
              >
                Continue
              </button>
            </div>
          </motion.div>
        </div>
      )}

      {/* Create Lead Modal */}
      <CreateLeadModal
        isOpen={showCreateLeadModal}
        onClose={() => setShowCreateLeadModal(false)}
        onLeadCreated={handleLeadCreated}
      />

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setLeadToDelete(null);
        }}
        onConfirm={() => handleDeleteLead(leadToDelete?.leadId)}
        title="Delete Lead"
        message="Are you sure you want to delete this lead? This action cannot be undone."
        itemName={leadToDelete ? `${leadToDelete.firstName} ${leadToDelete.lastName}` : ''}
        itemType="lead"
        isLoading={isDeleting}
        confirmText="Delete Lead"
        cancelText="Cancel"
      />

      {/* Dropdown Menu Portal */}
      {dropdownOpen && (
        <div 
          data-dropdown-menu
          className="fixed z-[9999] w-52 bg-white rounded-lg border border-gray-300 shadow-xl"
          style={{
            top: `${dropdownPosition.top}px`,
            right: `${dropdownPosition.right}px`
          }}
        >
          <div className="py-2">
            {(() => {
              const lead = leads.find(l => l.leadId === dropdownOpen);
              if (!lead) return null;
              
              return (
                <>
                  {lead.status !== 'lost' && (
                    <button
                      onClick={() => {
                        handleMarkAsLost(lead.leadId);
                        setDropdownOpen(null);
                      }}
                      className="flex items-center px-4 py-2.5 w-full text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                    >
                      <UserX className="mr-3 w-4 h-4" />
                      <span className="font-medium">Mark as Lost</span>
                    </button>
                  )}
                  {lead.status === 'contacted' && (
                    <button
                      onClick={() => {
                        handleStatusUpdate(lead.leadId, 'qualified');
                        setDropdownOpen(null);
                      }}
                      className="flex items-center px-4 py-2.5 w-full text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                    >
                      <UserCheck className="mr-3 w-4 h-4" />
                      <span className="font-medium">Mark as Qualified</span>
                    </button>
                  )}
                  {lead.status === 'qualified' && (
                    <button
                      onClick={() => {
                        handleConvertLead(lead);
                        setDropdownOpen(null);
                      }}
                      className="flex items-center px-4 py-2.5 w-full text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                    >
                      <UserCheck className="mr-3 w-4 h-4" />
                      <span className="font-medium">Convert & Create Trip</span>
                    </button>
                  )}
                  <button
                    onClick={() => {
                      handleExportLead(lead);
                      setDropdownOpen(null);
                    }}
                    className="flex items-center px-4 py-2.5 w-full text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    <Download className="mr-3 w-4 h-4" />
                    <span className="font-medium">Export Lead Data</span>
                  </button>
                  <hr className="my-2 border-gray-200" />
                  <button
                    onClick={() => openDeleteModal(lead)}
                    className="flex items-center px-4 py-2.5 w-full text-sm text-red-600 hover:bg-red-50 transition-colors"
                  >
                    <UserX className="mr-3 w-4 h-4" />
                    <span className="font-medium">Delete Lead</span>
                  </button>
                </>
              );
            })()}
          </div>
        </div>
      )}

      {/* Convert Lead with Trip Modal */}
      <ConvertLeadWithTripModal
        isOpen={showConvertModal}
        onClose={() => {
          setShowConvertModal(false);
          setLeadToConvert(null);
        }}
        lead={leadToConvert}
        onSuccess={handleConversionSuccess}
      />
    </div>
  );
};

export default CRMLeadsManagement;
